import React, { useEffect } from "react";
import PropTypes from "prop-types";
import articlesStore from "../../store/articles/ArticlesStore";
import { Grid } from "@material-ui/core";
import { sortOnPriority } from "../../store/articles/ArticlesHelpers";
import { Add, Remove, RemoveCircleOutline } from "@material-ui/icons";
import ControlPointIcon from "@material-ui/icons/ControlPoint";
import { updateArticle, updateArticles } from "../../store/articles/ArticlesApi";
import { getTagsString } from "../../store/articles/models/MenuTag";
import { makeStyles } from "@material-ui/core/styles";
import SimpleItemList from "./SimpleItemList";
import GetCurrency from "../../helper/GetCurrency";

const useStyles = makeStyles(() => ({
  root: {
    margin: "0 16px",
  },
  listItem: { padding: 0 },
}));

function ArticleTagEditor(props) {
  const classes = useStyles();

  function getArticlegroups() {
    return sortOnPriority(
      Object.keys(articlesStore.articlegroups).map((articlegroupKey) => articlesStore.articlegroups[articlegroupKey])
    );
  }

  const [articlegroups, setArticlegroups] = React.useState(getArticlegroups());

  useEffect(() => {
    function onMenuUpdated() {
      setArticlegroups(getArticlegroups());
    }

    articlesStore.on(articlesStore.events.MENU_UPDATED, onMenuUpdated);
    return () => {
      articlesStore.removeListener(articlesStore.events.MENU_UPDATED, onMenuUpdated);
    };
  }, []);

  const getTags = (article) => {
    if (props.type === "menu_tags") {
      return article.getMenuTags();
    } else if (props.type === "print_tags") {
      return article.getPrintTags();
    } else if (props.type === "supergroup_tags") {
      return article.getSupergroupTags();
    }
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        <SimpleItemList
          classes={classes}
          title={props.selectedTag}
          itemFields={[
            {
              name: "Show prices",
              key: "price",
              value: (value) => {
                return GetCurrency() + " " + (value / 100).toFixed(2);
              },
              valueType: "text",
            },
            { name: "Show plu", key: "plu", valueType: "text" },
            { name: "Show menukaarten", key: "menu_tags", color: "secondary", valueType: "array" },
            {
              name: "Show printgroups",
              key: "printTags",
              color: "primary",
              valueType: "array",
            },
          ]}
          groups={articlegroups.reduce((groups, articlegroup) => {
            let group = {
              name: articlegroup.name,
              id: articlegroup.id,
              actionButton: props.selectedTag && <RemoveCircleOutline />,
              items: articlegroup.getArticles(articlesStore.articles).reduce((items, article) => {
                let tags = getTags(article);
                if (article.active && (tags.length === 0 || tags.includes(props.selectedTag))) {
                  let item = {
                    name: article.name,
                    id: article.id,
                    supergroup_tags: props.type === "supergroup_tags" ? tags : article.getSupergroupTags(),
                    menu_tags: props.type === "menu_tags" ? tags : article.getMenuTags(),
                    printTags: props.type === "print_tags" ? tags : article.getPrintTags(),
                    price: article.price,
                    plu: article.plu,
                    searchTags: [article.name, article.id, article.plu, articlegroup.name],
                    actionButton: getTags(article).includes(props.selectedTag) ? <Remove /> : null,
                  };
                  items.push(item);
                }
                return items;
              }, []),
            };
            if (group.items.length > 0) {
              groups.push(group);
            }
            return groups;
          }, [])}
          onActionButton={(itemId) => {
            let article = articlesStore.articles[itemId];
            let tags = getTags(article);
            tags = tags.filter((tag) => {
              return tag !== props.selectedTag;
            });

            updateArticle(articlesStore.merchantId, article, {
              [props.type === "menu_tags"
                ? "menu_tags"
                : props.type === "print_tags"
                ? "printTags"
                : "supergroup_tags"]: getTagsString(tags),
            });
          }}
          onGroupActionButton={(itemIds) => {
            let newData = [];
            let oldData = [];
            let articles = [];
            itemIds.forEach((itemId) => {
              let article = articlesStore.articles[itemId];
              articles.push(article);
              let tags = getTags(article);
              oldData.push({
                [props.type === "menu_tags"
                  ? "menu_tags"
                  : props.type === "print_tags"
                  ? "printTags"
                  : "supergroup_tags"]: getTagsString(tags),
              });
              tags = tags.filter((tag) => {
                return tag !== props.selectedTag;
              });
              newData.push({
                [props.type === "menu_tags"
                  ? "menu_tags"
                  : props.type === "print_tags"
                  ? "printTags"
                  : "supergroup_tags"]: getTagsString(tags),
              });
            });

            updateArticles(articlesStore.merchantId, articles, newData, oldData);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SimpleItemList
          classes={classes}
          title={"Alle artikelen"}
          itemFields={[
            {
              name: "Show prices",
              key: "price",
              value: (value) => {
                return GetCurrency() + " " + (value / 100).toFixed(2);
              },
              valueType: "text",
            },
            { name: "Show plu", key: "plu", valueType: "text" },
            { name: "Show menukaarten", key: "menu_tags", color: "secondary", valueType: "array" },
            {
              name: "Show printgroups",
              key: "printTags",
              color: "primary",
              valueType: "array",
            },
          ]}
          groups={articlegroups.reduce((groups, articlegroup) => {
            let group = {
              name: articlegroup.name,
              id: articlegroup.id,
              items: articlegroup.getArticles(articlesStore.articles).reduce((items, article) => {
                let tags = getTags(article);
                if (article.active && !tags.includes(props.selectedTag)) {
                  let item = {
                    name: article.name,
                    id: article.id,
                    supergroup_tags: props.type === "supergroup_tags" ? tags : article.getSupergroupTags(),
                    menu_tags: props.type === "menu_tags" ? tags : article.getMenuTags(),
                    printTags: props.type === "print_tags" ? tags : article.getPrintTags(),
                    price: article.price,
                    plu: article.plu,
                    searchTags: [article.name, article.id, article.plu, articlegroup.name],
                    actionButton: props.selectedTag != null && !getTags(article).includes(props.selectedTag) && <Add />,
                  };
                  items.push(item);
                }
                return items;
              }, []),
              actionButton: props.selectedTag && <ControlPointIcon />,
            };
            if (group.items.length > 0) {
              groups.push(group);
            }
            return groups;
          }, [])}
          onActionButton={(itemId) => {
            let article = articlesStore.articles[itemId];
            let tags = getTags(article);
            tags.push(props.selectedTag);
            updateArticle(articlesStore.merchantId, article, {
              [props.type === "menu_tags"
                ? "menu_tags"
                : props.type === "print_tags"
                ? "printTags"
                : "supergroup_tags"]: getTagsString(tags),
            });
          }}
          onGroupActionButton={(itemIds) => {
            let newData = [];
            let oldData = [];
            let articles = [];
            itemIds.forEach((itemId) => {
              let article = articlesStore.articles[itemId];
              articles.push(article);
              let tags = getTags(article);
              oldData.push({
                [props.type === "menu_tags"
                  ? "menu_tags"
                  : props.type === "print_tags"
                  ? "printTags"
                  : "supergroup_tags"]: getTagsString(tags),
              });
              tags.push(props.selectedTag);
              newData.push({
                [props.type === "menu_tags"
                  ? "menu_tags"
                  : props.type === "print_tags"
                  ? "printTags"
                  : "supergroup_tags"]: getTagsString(tags),
              });
            });

            updateArticles(articlesStore.merchantId, articles, newData, oldData);
          }}
        />
      </Grid>
    </Grid>
  );
}

ArticleTagEditor.propTypes = {
  type: PropTypes.oneOf(["print_tags", "menu_tags", "supergroup_tags"]).isRequired,
  selectedTag: PropTypes.string.isRequired,
};

export default ArticleTagEditor;
